import { memo, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isValidUrl } from '../../../services/url-validation'
import { Skeleton } from '../Skeleton'

// Hoist static JSX to avoid re-creation on every render (rendering-hoist-jsx)
const PLAY_ICON = (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    className="audio-atom__icon"
    aria-hidden="true"
    role="img">
    <path d="M8 5v14l11-7z" />
  </svg>
)

const PAUSE_ICON = (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    className="audio-atom__icon"
    aria-hidden="true"
    role="img">
    <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
  </svg>
)

const STOP_ICON = (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    className="audio-atom__icon"
    aria-hidden="true"
    role="img">
    <path d="M6 6h12v12H6z" />
  </svg>
)

const MUTE_ICON = (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    className="audio-atom__icon"
    aria-hidden="true"
    role="img">
    <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.03c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
  </svg>
)

const UNMUTE_ICON = (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    className="audio-atom__icon"
    aria-hidden="true"
    role="img">
    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
  </svg>
)

export interface AudioProps extends Omit<React.AudioHTMLAttributes<HTMLAudioElement>, 'src'> {
  src?: string | null
  label?: string
  error?: string
  // Shows a pulsing placeholder in the player's shape while the stream URL loads
  skeleton?: boolean
}

export const Audio = memo(({ label, error, className, src, skeleton, ...props }: AudioProps) => {
  const { t } = useTranslation('admin-mobifull')
  const audioId = useId()
  const audioRef = useRef<HTMLAudioElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isMuted, setIsMuted] = useState(false)
  const [volume, setVolume] = useState(1)
  const [hasError, setHasError] = useState(false)

  // Memoize derived boolean (rerender-derived-state)
  const isValidStreamUrl = useMemo(() => isValidUrl(src ?? ''), [src])

  // Reset state on src change — src is the trigger, not used inside the callback
  // biome-ignore lint/correctness/useExhaustiveDependencies: src is intentionally the trigger for resetting derived state
  useEffect(() => {
    setHasError(false)
    setIsPlaying(false)
  }, [src])

  // Use functional updates and stable callbacks (rerender-functional-setstate)
  const togglePlay = useCallback(() => {
    const audio = audioRef.current
    if (!audio) {
      return
    }

    if (isPlaying) {
      audio.pause()
    } else {
      audio.play().catch(() => setHasError(true))
    }
    setIsPlaying(prev => !prev)
  }, [isPlaying])

  const stop = useCallback(() => {
    const audio = audioRef.current
    if (!audio) {
      return
    }

    audio.pause()
    audio.currentTime = 0
    // For live streams, we might need to reset the src to truly stop/reset the buffer
    const currentSrc = audio.src
    audio.src = ''
    audio.load()
    audio.src = currentSrc

    setIsPlaying(false)
  }, [])

  const toggleMute = useCallback(() => {
    const audio = audioRef.current
    if (!audio) {
      return
    }

    audio.muted = !isMuted
    setIsMuted(prev => !prev)
  }, [isMuted])

  const handleVolumeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = Number.parseFloat(e.target.value)
    setVolume(newVolume)
    if (audioRef.current) {
      audioRef.current.volume = newVolume
    }
  }, [])

  const handleError = useCallback(() => {
    setHasError(true)
    setIsPlaying(false)
  }, [])

  // Early exits AFTER hooks
  if (skeleton) {
    return (
      <div className="audio-atom">
        {label && <span className="audio-atom__label">{label}</span>}
        <Skeleton shape="audio" />
      </div>
    )
  }
  if (!src || !isValidStreamUrl) {
    return null
  }

  const containerClasses = ['audio-atom', hasError && 'audio-atom--error', className]
    .filter(Boolean)
    .join(' ')

  const statusText = hasError
    ? t('audio.status.error')
    : isPlaying
      ? t('audio.status.playing')
      : t('audio.status.ready')

  return (
    <div className={containerClasses}>
      {label && (
        <label htmlFor={audioId} className="audio-atom__label">
          {label}
        </label>
      )}

      <div className="audio-atom__wrapper">
        <audio
          id={audioId}
          ref={audioRef}
          src={src}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onError={handleError}
          className="audio-atom__native"
          {...props}
        />

        <div className="audio-atom__controls">
          <button
            type="button"
            onClick={togglePlay}
            className="audio-atom__btn"
            aria-label={isPlaying ? t('audio.actions.pause') : t('audio.actions.play')}
            disabled={hasError}>
            {isPlaying ? PAUSE_ICON : PLAY_ICON}
          </button>

          <button
            type="button"
            onClick={stop}
            className="audio-atom__btn audio-atom__stop-btn"
            aria-label={t('audio.actions.stop')}
            disabled={hasError || (!isPlaying && audioRef.current?.currentTime === 0)}>
            {STOP_ICON}
          </button>

          <div className="audio-atom__info">
            <span className="audio-atom__status" title={statusText}>
              {statusText}
            </span>
            {!hasError && (
              <span className="audio-atom__source" title={src}>
                {src}
              </span>
            )}
          </div>

          <div className="audio-atom__volume">
            <button
              type="button"
              onClick={toggleMute}
              className="audio-atom__btn"
              aria-label={isMuted ? t('audio.actions.unmute') : t('audio.actions.mute')}
              disabled={hasError}>
              {isMuted ? MUTE_ICON : UNMUTE_ICON}
            </button>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={volume}
              onChange={handleVolumeChange}
              className="audio-atom__slider"
              aria-label={t('audio.actions.volume')}
              disabled={hasError}
            />
          </div>
        </div>
      </div>

      {(error || (hasError && !error)) && (
        <span className="audio-atom__error-msg">{error || t('audio.errorGeneric')}</span>
      )}
    </div>
  )
})

Audio.displayName = 'Audio'
